import React, { useState } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, Linking, Image } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { Building2, Check, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import SupermedicLogo from '../../../assets/images/supermedic-logo-web.png';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../../constants/webStyles';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { login } from '../../../services/auth';
import { setLoggedIn } from '../../../reducers/auth/user';
import api from '../../../services/api';
import { buildVersion } from '../../../../App';
import BrandingPanelDoctor from '../DoctorAuth/components/BrandingPanel.web';
import BrandingPanelFacility from '../FacilityAuth/components/BrandingPanel.web';

const LoginScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { isDoctor } = useSelector((state) => state.user);
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const loginMutation = useMutation(login, {
    onError: (err) => {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    },
    onSuccess: (res) => {
      const token = res.data.data.token;
      const slug = res.data.data.slug;
      const country = res.data.data.country;

      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      dispatch(setLoggedIn({ token, slug, country }));
      navigation.navigate('HomeScreen');
    },
  });

  const handleLogin = () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    loginMutation.mutate({
      email,
      password,
      scope: isDoctor ? 'doctor' : 'clinic_owner',
    });
  };

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Left Side - Branding */}
      {isDoctor ? <BrandingPanelDoctor /> : <BrandingPanelFacility />}

      {/* Right Side - Login Form */}
      <View style={[styles.formSide, isMobile && styles.formSideMobile]}>
        <View style={styles.formContainer}>
          {/* Logo */}
          <View style={styles.formLogo}>
            <Image
              source={SupermedicLogo}
              style={styles.logoImage}
              resizeMode="contain"
            />
          </View>

          {/* Login Form */}
          <View style={styles.form}>
            {error ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <View style={styles.inputWrapper}>
              <View style={styles.inputIcon}>
                <Mail size={18} color={webColors.textMuted} />
              </View>
              <TextInput
                style={styles.input}
                placeholder="Email"
                placeholderTextColor={webColors.textMuted}
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={!loginMutation.isLoading}
              />
            </View>

            <View style={styles.inputWrapper}>
              <View style={styles.inputIcon}>
                <Lock size={18} color={webColors.textMuted} />
              </View>
              <TextInput
                style={[styles.input, styles.inputWithToggle]}
                placeholder="Password"
                placeholderTextColor={webColors.textMuted}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                editable={!loginMutation.isLoading}
                onSubmitEditing={handleLogin}
              />
              <TouchableOpacity
                style={styles.togglePassword}
                onPress={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff size={18} color={webColors.textMuted} />
                ) : (
                  <Eye size={18} color={webColors.textMuted} />
                )}
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.forgotLink}
              onPress={() => navigation.navigate('ForgotPasswordScreen')}
            >
              <Text style={styles.forgotLinkText}>Forgot Password</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.loginBtn, loginMutation.isLoading && styles.loginBtnDisabled]}
              onPress={handleLogin}
              disabled={loginMutation.isLoading}
            >
              <Text style={styles.loginBtnText}>
                {loginMutation.isLoading ? 'Signing in...' : 'Sign in'}
              </Text>
            </TouchableOpacity>

            <View style={styles.createAccountLink}>
              <Text style={styles.createAccountText}>Don't have an account?</Text>
              <TouchableOpacity onPress={() => navigation.navigate(isDoctor ? 'DoctorAuth' : 'FacilityAuth')}>
                <Text style={styles.createAccountLinkText}>Create an account</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.loginFooter}>
            <Text style={styles.versionText}>v{buildVersion}</Text>
            <TouchableOpacity onPress={() => Linking.openURL('mailto:support@supermedic.health')}>
              <Text style={styles.supportEmail}>support@supermedic.health</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
  },
  containerMobile: {
    flexDirection: 'column',
  },
  // Brand Side (Left)
  brandSide: {
    flex: 1,
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2557a7 50%, #3b82f6 100%)',
    backgroundColor: webColors.secondary,
    padding: 60,
    justifyContent: 'center',
  },
  brandContent: {
    maxWidth: 500,
  },
  brandIcon: {
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 36,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 16,
    lineHeight: 44,
  },
  brandSubtitle: {
    fontSize: 18,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 28,
    marginBottom: 40,
  },
  brandFeatures: {
    gap: 16,
  },
  brandFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  brandFeatureIcon: {
    width: 32,
    height: 32,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureText: {
    fontSize: 16,
    color: '#ffffff',
  },
  // Form Side (Right)
  formSide: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formSideMobile: {
    padding: 20,
  },
  formContainer: {
    maxWidth: 480,
    width: '100%',
  },
  formLogo: {
    alignItems: 'center',
    marginBottom: 48,
  },
  logoImage: {
    width: 280,
    height: 90,
  },
  form: {
    gap: 16,
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 8,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: 8,
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    zIndex: 1,
  },
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 16,
    paddingLeft: 48,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  inputWithToggle: {
    paddingRight: 48,
  },
  togglePassword: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    padding: 4,
  },
  forgotLink: {
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
  forgotLinkText: {
    color: webColors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  loginBtn: {
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
    marginBottom: 32,
  },
  loginBtnDisabled: {
    opacity: 0.6,
  },
  loginBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  createAccountLink: {
    alignItems: 'center',
    marginBottom: 32,
  },
  createAccountText: {
    fontSize: 15,
    color: webColors.textMuted,
    marginBottom: 4,
  },
  createAccountLinkText: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.primary,
  },
  loginFooter: {
    alignItems: 'center',
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  versionText: {
    fontSize: 12,
    color: webColors.textMuted,
    marginBottom: 8,
  },
  supportEmail: {
    fontSize: 14,
    color: webColors.textMuted,
  },
});

export default LoginScreen;
